import { useContext, useEffect, useState } from 'react';
import { DataContext, RouteContext } from 'entries/common/widgets/context';
import { useSnackbar } from 'fronton-react';
import { useSetRecoilState } from 'recoil';
import { getCookie } from 'utils/cookie.helper';
import { TRoomsParams } from 'common/types/floor-params.type';
import { calculatedRoomsParams, roomArea } from 'pages/common/store/rooms';
import { useSurfaces } from '../../surface/hooks/use-surfaces';
import { useSurfaceState } from '../../surface/store/hooks/surface';
import { useMaterialState } from '../../surface/store/hooks/material';
import { dimensionsAtom, dimensionsFormValues } from '../../dimensions/store/dimensions';
import { parseProjectConfiguration } from '../helpers/parse-project-configuration-parameters';
import { getFormData } from '../../dimensions/helpers/get-form-data';
import { Inputs } from '../../dimensions/types';
import { getFormBody } from '../../dimensions/helpers/get-body';
import { getFormattedCalculateParams } from '../../dimensions/helpers/get-formatted-calculate-params.helper';
import { SNACKBARS_DICTIONARY, TSnackbarDictionaryKeys } from '../../common/components/Snackbar';
import { hasProjectConfigurationData } from '../helpers/has-project-configuration-data';
import { userProjectConfigurationCookieNames } from '../../project-configuration/const';

export const useGetCalculateData = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const data = useContext(DataContext);
  const { setContext: setRoute } = useContext(RouteContext);
  const { addSnack } = useSnackbar();
  const surfacesData = useSurfaces();
  const { surfaceFormState } = useSurfaceState();
  const { materialFormState } = useMaterialState();
  const saveCalculateParams = useSetRecoilState(dimensionsAtom);
  const setDimensionState = useSetRecoilState(dimensionsFormValues);
  const setCalculatedRoomParams = useSetRecoilState<TRoomsParams>(calculatedRoomsParams);
  const setIsAreaEnabled = useSetRecoilState(roomArea);

  useEffect(() => {
    const api = data?.api;
    const hasProjectData = hasProjectConfigurationData(data);

    if (api && hasProjectData) {
      const inputsData = parseProjectConfiguration(data?.projectConfigurationParameters ?? []);
      const formData = getFormData({
        data: (inputsData as unknown) as Inputs,
        state: {
          isDoorsWindowsArea: false,
          isRoomArea: !(inputsData.length && inputsData.width),
        },
      });
      const isParamsHasOnlySquare = !formData.length && !!formData.area;
      const body = getFormBody({
        data: formData,
        surfaces: { state: surfaceFormState, data: surfacesData },
        materials: materialFormState,
        productData: data,
      });
      setDimensionState(formData);
      setIsAreaEnabled(isParamsHasOnlySquare);
      setCalculatedRoomParams({
        ...inputsData,
        id: getCookie(userProjectConfigurationCookieNames.LAMINATE) ?? '',
        createdDate: '',
        ...(Boolean(body.inputParameters.areaUser) && {
          square: body.inputParameters.areaUser,
        }),
        ...(Boolean(body.inputParameters.length) && { length: body.inputParameters.length }),
        ...(Boolean(body.inputParameters.width) && { width: body.inputParameters.width }),
      } as TRoomsParams);

      api
        .post(`/calculate/${data.productId}`, body)
        .then(calculateParams => {
          const formattedCalculateParams = getFormattedCalculateParams(calculateParams);
          saveCalculateParams(formattedCalculateParams);
          setRoute('calculation');
        })
        .catch(() => {
          setTimeout(() => {
            addSnack(SNACKBARS_DICTIONARY[TSnackbarDictionaryKeys.CALCULATION_WITH_DEFAULT_PARAMS_ERROR]);
          }, 0);
          setRoute('dimensions');
        })
        .finally(() => {
          setIsLoaded(true);
        });
    } else {
      setIsLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Нам не нужны лишние запуски
  }, [data]);

  return {
    isLoaded,
  };
};
